import {
  type ClientMsg,
  ClientMsgKind,
  type EngineCtx,
  Phase,
  type Rng,
  type Room,
  reduce,
  type ServerMsg,
  ServerMsgKind,
} from '@infiltre/shared';
import type { KvStorage } from '@/shared/lib/storage';
import { Emitter } from './emitter';
import { DriverMode, type DriverStatus, DriverStatusKind, type GameDriver } from './types';

export interface LocalGameDriverConfig {
  storage: KvStorage;
  storageKey: string;
  rng: Rng;
  initialState: Room;
}

export class LocalGameDriver implements GameDriver {
  readonly mode = DriverMode.Standalone;

  private state: Room;
  private currentStatus: DriverStatus = { kind: DriverStatusKind.Idle };
  private readonly messages = new Emitter<ServerMsg>();
  private readonly statusEvents = new Emitter<DriverStatus>();
  /** Mirrors server's round-timer: auto-fires OpenVote from host when discuss deadline elapses. */
  private discussTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly config: LocalGameDriverConfig) {
    this.state = config.initialState;
  }

  get currentState(): Room {
    return this.state;
  }

  status(): DriverStatus {
    return this.currentStatus;
  }

  async start(): Promise<void> {
    this.setStatus({ kind: DriverStatusKind.Ready });
    this.messages.emit({ kind: ServerMsgKind.RoomState, room: this.state });
    // Resume case: bootstrap may already be mid-Discuss with a future deadline.
    this.scheduleDiscussTimer();
  }

  async stop(): Promise<void> {
    this.clearDiscussTimer();
    this.setStatus({ kind: DriverStatusKind.Idle });
    this.messages.clear();
    this.statusEvents.clear();
  }

  send(msg: ClientMsg): void {
    const ctx: EngineCtx = {
      now: Date.now(),
      rng: this.config.rng,
      authorize: () => true,
    };
    const { state, emitted } = reduce(this.state, msg, ctx);
    this.state = state;
    this.persist();
    for (const m of emitted) this.messages.emit(m);
    this.scheduleDiscussTimer();
  }

  onMessage(handler: (m: ServerMsg) => void) {
    return this.messages.on(handler);
  }

  onStatus(handler: (s: DriverStatus) => void) {
    return this.statusEvents.on(handler);
  }

  private setStatus(s: DriverStatus): void {
    this.currentStatus = s;
    this.statusEvents.emit(s);
  }

  private persist(): void {
    this.config.storage.set(this.config.storageKey, this.state);
  }

  private scheduleDiscussTimer(): void {
    this.clearDiscussTimer();
    if (this.state.phase !== Phase.Discuss) return;
    const deadline = this.state.discussDeadlineAt;
    if (deadline === undefined) return;
    const delay = Math.max(0, deadline - Date.now());
    this.discussTimer = setTimeout(() => this.fireDiscussTimeout(), delay);
  }

  private clearDiscussTimer(): void {
    if (this.discussTimer === null) return;
    clearTimeout(this.discussTimer);
    this.discussTimer = null;
  }

  private fireDiscussTimeout(): void {
    this.discussTimer = null;
    if (this.state.phase !== Phase.Discuss) return;
    const host = this.state.players.find((p) => p.isHost);
    if (!host) return;
    this.send({ kind: ClientMsgKind.OpenVote, actorPlayerId: host.id });
  }
}

// v3: bumped when `Room.nextRoundRequests` became required; legacy v2 blobs are dropped.
export const STANDALONE_STORAGE_KEY = 'infiltre:standalone:state:v3';

export const loadStandaloneState = (storage: KvStorage): Room | null =>
  storage.get<Room>(STANDALONE_STORAGE_KEY);

export const clearStandaloneState = (storage: KvStorage): void =>
  storage.remove(STANDALONE_STORAGE_KEY);
